import { ref, computed } from "vue";

const COOKIE_CONSENT_NAME = "cnmc_cookie_consent";
const COOKIE_PREFERENCES_NAME = "cnmc_cookie_preferences";
const COOKIE_EXPIRY_DAYS = 365; // 1 año

// Estado reactivo global
const showBanner = ref(false);
const showConfigModal = ref(false);
const preferences = ref({
  analytics: false,
});

// ============================================
// FUNCIONES DE COOKIES (no localStorage)
// ============================================

// Obtener valor de una cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
};

// Establecer una cookie
const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}; ${expires}; path=/; SameSite=Lax; Secure`;
};

// Eliminar una cookie
const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.cnmc.gob.es`;
};

// ============================================
// COMPOSABLE
// ============================================

export function useCookieConsent() {
  // Inicializar estado
  const initCookieConsent = () => {
    const consent = getCookie(COOKIE_CONSENT_NAME);
    const savedPreferences = getCookie(COOKIE_PREFERENCES_NAME);

    if (!consent) {
      // No hay consentimiento previo - mostrar banner
      showBanner.value = true;
    } else {
      // Hay consentimiento - cargar preferencias
      if (savedPreferences) {
        try {
          preferences.value = JSON.parse(decodeURIComponent(savedPreferences));
        } catch {
          // Error parsing preferences - usando valores por defecto
        }
      }

      // Cargar Google Analytics si está aceptado
      if (preferences.value.analytics) {
        loadGoogleAnalytics();
      }
    }
  };

  // Aceptar todas las cookies
  const acceptAll = () => {
    preferences.value.analytics = true;
    savePreferences();
    showBanner.value = false;
    showConfigModal.value = false;
    loadGoogleAnalytics();
  };

  // Rechazar todas las cookies (solo las no esenciales)
  const rejectAll = () => {
    preferences.value.analytics = false;
    savePreferences();
    showBanner.value = false;
    showConfigModal.value = false;
    removeGoogleAnalytics();
  };

  // Guardar preferencias personalizadas
  const saveCustomPreferences = () => {
    savePreferences();
    showBanner.value = false;
    showConfigModal.value = false;

    if (preferences.value.analytics) {
      loadGoogleAnalytics();
    } else {
      removeGoogleAnalytics();
    }
  };

  // Guardar en cookies (NO localStorage)
  const savePreferences = () => {
    setCookie(COOKIE_CONSENT_NAME, "true", COOKIE_EXPIRY_DAYS);
    setCookie(
      COOKIE_PREFERENCES_NAME,
      encodeURIComponent(JSON.stringify(preferences.value)),
      COOKIE_EXPIRY_DAYS
    );
  };

  // Abrir modal de configuración
  const openConfigModal = () => {
    showConfigModal.value = true;
    showBanner.value = false;
  };

  // Cargar Google Analytics usando el plugin existente
  const loadGoogleAnalytics = () => {
    // Usar la función global del plugin
    if (globalThis.loadGoogleAnalytics) {
      globalThis.loadGoogleAnalytics();
    }
  };

  // Eliminar Google Analytics (cookies y scripts)
  const removeGoogleAnalytics = () => {
    // Usar la función global del plugin si existe
    if (globalThis.disableGoogleAnalytics) {
      globalThis.disableGoogleAnalytics();
    }

    // Eliminar cookies de Google Analytics
    for (const rawCookie of document.cookie.split(";")) {
      const cookieName = rawCookie.trim().split("=")[0];

      // Eliminar cookies que empiecen con _ga
      if (cookieName.startsWith("_ga")) {
        deleteCookie(cookieName);
      }
    }
  };

  // Resetear consentimiento (útil para testing)
  const resetConsent = () => {
    deleteCookie(COOKIE_CONSENT_NAME);
    deleteCookie(COOKIE_PREFERENCES_NAME);
    preferences.value.analytics = false;
    showBanner.value = true;
    removeGoogleAnalytics();
  };

  // Computed para saber si hay consentimiento
  const hasConsent = computed(() => {
    return getCookie(COOKIE_CONSENT_NAME) === "true";
  });

  return {
    // Estado
    showBanner,
    showConfigModal,
    preferences,
    hasConsent,

    // Métodos
    initCookieConsent,
    acceptAll,
    rejectAll,
    saveCustomPreferences,
    openConfigModal,
    resetConsent,
  };
}
